import { sortAsc } from '@strata/helper-functions';
import { WarningDialog } from '@strata/react-dialog';
import { FormContainer } from '@strata/react-form-container';
import { TextInputFormik } from '@strata/react-inputs';
import { SuggestMultiSelectFormik } from '@strata/react-selects';
import { dialogStrings, validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import * as Yup from 'yup';

import { CREATE_ROUTE } from 'services/constants';
import { handleError } from 'services/ErrorManagement';
import { put, useGet } from 'services/Http';

const OID_HAS_PARENT = { secondaryData: 'Associated with a parent product', disabled: true };

export const ParentProductServiceDetails = () => {
    const { parentProductServiceId } = useParams();
    const navigate = useNavigate();

    const goHome = () => navigate('/parent-products');

    const isCreate = parentProductServiceId === CREATE_ROUTE;

    const parentProductService = useGet(
        !isCreate ? `/reference/parent_product_services/${parentProductServiceId}` : null,
    );

    const productServices = useGet('/reference/productservices/summaryonly');

    const [showWarning, setShowWarning] = useState(false);

    return (
        <Formik
            initialValues={{
                parentProductService: parentProductService?.parent_product_service || '',
                displayName: parentProductService?.display_name || '',
                oids: parentProductService?.oids || [],
            }}
            enableReinitialize
            validationSchema={Yup.object({
                parentProductService: Yup.string()
                    .max(50, validationStrings.maxCharacter[50])
                    .required(validationStrings.requiredField.errorMessage),
                displayName: Yup.string()
                    .max(50, validationStrings.maxCharacter[50])
                    .required(validationStrings.requiredField.errorMessage),
                oids: Yup.array(),
            })}
            onSubmit={async (values) => {
                try {
                    await put(`/reference/parent_product_services/${values.parentProductService}`, {
                        display_name: values.displayName,
                        oids: values.oids.map((item) => item?.name ?? item),
                    });
                    goHome();
                } catch (err) {
                    handleError(`Unable to ${isCreate ? 'create' : 'update'} parent product.`, err);
                }
            }}
        >
            {(formik) => (
                <>
                    <WarningDialog
                        dialogTitle={dialogStrings.unsavedChanges.title}
                        isOpen={showWarning}
                        onSecondaryButtonClick={() => setShowWarning(false)}
                        onPrimaryButtonClick={goHome}
                        dialogContent={<p>{dialogStrings.unsavedChanges.body}</p>}
                    />
                    <FormContainer
                        formTitle={`${isCreate ? 'Create' : 'Edit'} Parent Product`}
                        isLoading={!isCreate && !parentProductService}
                        onFormSubmit={formik.handleSubmit}
                        isSubmitting={formik.isSubmitting || !(formik.dirty && formik.isValid)}
                        onSecondaryButtonClick={() => {
                            if (formik.dirty) {
                                setShowWarning(true);
                            } else {
                                goHome();
                            }
                        }}
                        formLeftContent={
                            <>
                                <TextInputFormik
                                    label="Parent Product Name"
                                    id="parentProductService"
                                    required
                                    disabled={!isCreate}
                                />
                                <TextInputFormik
                                    label="Full Display Name"
                                    id="displayName"
                                    required
                                />
                                <SuggestMultiSelectFormik
                                    label="Product Oid(s)"
                                    id="oids"
                                    placeholderText="Search Product OIDs..."
                                    values={(productServices ?? [])
                                        .map((ps) => ({
                                            name: ps.oid,
                                            ...(ps.parent_product_service &&
                                            !parentProductService?.oids.includes(ps.oid)
                                                ? OID_HAS_PARENT
                                                : {}),
                                        }))
                                        .sort(({ name: a }, { name: b }) => sortAsc(a, b))}
                                />
                            </>
                        }
                    />
                </>
            )}
        </Formik>
    );
};
